import { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from "react-redux";
import toast from 'react-hot-toast';
import { 
    Box, 
    Typography, 
    CircularProgress,
    FormControl, 
    MenuItem, 
    InputLabel, 
    Select,       
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import api from 'state/axios';
import { filterCrewMember } from "state/helpers";
import { countCrewMembers, countItemsByTableau } from './utils';
import Header from 'components/Header';
import ScrollToTop from 'components/ScrollToTop';
import FlexBetween from 'components/FlexBetween';
import FilterButton from 'components/FilterButton';
import ChartDialog from 'components/chartNivo/ChartDialog';
import CrewFilterDialog from 'components/CrewFilterDialog';
import CommonTableau from './CommonTableau';
import SortBarMultiLinesChart from 'components/chartNivo/SortBarMultiLinesChart';
import SortStackedBarChart from 'components/chartNivo/SortStackedBarChart';
import { boxStyles as styles } from 'components/CustomStyles';


const displayOptions = [
    { value: "chart", label: "Chart" },
    { value: "tableau", label: "Tableau" },
];

const viewOptions = {
    chart: [
        { value: "crewByRankBase", label: "by Rank & Base" },
        { value: "crewByRank", label: "by Rank" },
        { value: "crewByBase", label: "by Base" },
        { value: "crewByGender", label: "by Gender" },
        { value: "crewByNationality", label: "by Nationality" },
    ],
    tableau: [
        { value: "byRankBase", label: "by Rank & Base" },
        { value: "byRank", label: "by Rank" },
        { value: "byBase", label: "by Base" },
        { value: "byGender", label: "by Gender" },
        { value: "byNationality", label: "by Nationality" },
    ],
};

const FlyingCrewCountChart = () => {    
    useAuthCheck();
    
    const scrollRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [chartOpen, setChartOpen] = useState(false); 
    const [selectedChart, setSelectedChart] = useState(null);
    const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
    const [filters, setFilters] = useState({ isActive: 'ALL' });
    const defaultFilters = {
        isActive: 'ALL',
    };
    
    const [displayType, setDisplayType] = useState('chart');
    const [view, setView] = useState('crewByRankBase');

    useEffect(() => {
        const options = viewOptions[displayType];
        if (!options?.length) return;
        const isValid = options.some(opt => opt.value === view);
        if (!isValid) setView(options[0].value);
    }, [displayType, view]);

    const safeView = useMemo(() => {
        const options = viewOptions[displayType] || [];
        return options.some(o => o.value === view) ? view : options[0]?.value || '';
    }, [view, displayType]);
    const [data, setData] = useState({
        crewByRankBase: [],
        crewByRank: [],           
        crewByBase: [],           
        crewByGender: [],        
        crewByStackedGender: [],   
        crewByNationality: [],   
    });
    
    const crewRanksData = useSelector((state) => state.crewRanks);    
    const [crewMembers, setCrewMembers] = useState([]);

    useEffect(() => {
        const fetchCrewMembers = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const url = '/crewmembers';
                const res = await api.get(url);

                const updatedData = res.data
                    ?.filter(member => member.isActive)
                    .map(member => {
                        const { rank, location, ...rest } = member;
                        return {
                            ...rest,
                            post: rank,
                            base: location
                        };
                    });


                setCrewMembers(updatedData);
            } catch (error) {
                setError(error);
                toast.error(error.message || "Failed to fetch crew members!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchCrewMembers();      
    }, []); 
        
    const filterCrewMembers = useMemo(() => {
        if (!Array.isArray(crewMembers)) {
            return []; 
        }
        
        if (!filters) return crewMembers; 

        return crewMembers.filter(member => filterCrewMember(member, filters));
    }, [crewMembers, filters]);  
    
    useEffect(() => {      
        try {
            const results = {
                crewByRankBase: countCrewMembers(filterCrewMembers, 'post&base'),
                crewByRank: countCrewMembers(filterCrewMembers, 'post'), 
                crewByBase: countCrewMembers(filterCrewMembers, 'base'), 
                crewByGender: countCrewMembers(filterCrewMembers, 'gender'), 
                crewByStackedGender: countCrewMembers(filterCrewMembers, 'stackedgender', 'stackedbar'), 
                crewByNationality: countCrewMembers(filterCrewMembers, 'nationality'), 
            };
            
            setData(results);
        } catch(error) {
            setError(error);
        } 
    }, [filterCrewMembers, filters]); 

    const tableauData = useMemo(() => {
        if (displayType !== 'tableau') return [];
        const fieldMap = {
            byRankBase:   ['post', 'base'],
            byRank:       ['post', null],
            byBase:       ['base', null],
            byGender:     ['gender', null],
            byNationality:['nationality', null],
        };
        const fields = fieldMap[safeView];
        if (!fields) return [];
        return countItemsByTableau(filterCrewMembers, fields[0], fields[1]);
    }, [displayType, safeView, filterCrewMembers]);

    const handleFilterOpen = () => setIsFilterDialogOpen(true);
    const handleFilterClose = () => setIsFilterDialogOpen(false);

    const handleFilterApply = (filterValues) => {
        setFilters(filterValues);
    };

    const {
        baseOptions,
        postOptions,
        trainingQualOptions,
        isActiveOptions,
    } = useMemo(() => {
        if (!Array.isArray(crewMembers) || crewMembers.length === 0) {
            return {
                baseOptions: [],
                postOptions: [],
                trainingQualOptions: [],
                isActiveOptions: [],
            };
        }

        const rankMap = new Map(
            (crewRanksData ?? []).map(rank => [rank.rankCode, rank])
        );

        const baseSet = new Set();
        const postSet = new Set();
        const trainingQualSet = new Set();
        const isActiveSet = new Set();

        crewMembers.forEach(m => {
            if (m.base) baseSet.add(m.base);
            if (m.post) postSet.add(m.post);
            if (m.isActive !== undefined) isActiveSet.add(m.isActive);

            m.trainingQuals?.forEach(q => q && trainingQualSet.add(q));
        });

        const baseOptions = [...baseSet].sort();

        const postOptions = [...postSet]
            .map(post => rankMap.get(post))
            .filter(Boolean)
            .sort((a, b) => a.rankPriority - b.rankPriority)
            .map(rank => rank.rankCode);

        const trainingQualOptions = [...trainingQualSet].sort();

        const isActiveOptions = [...isActiveSet].sort();

        return {
            baseOptions,
            postOptions,
            trainingQualOptions,
            isActiveOptions,
        };
    }, [crewMembers, crewRanksData]);
    
    const handleChartOpen = (chart) => {
        setSelectedChart(
            <Box sx={{ ...styles }}>
                <Box 
                    height="100%" 
                    width={"100%"} 
                    sx={chart.style}
                >
                    {chart.component}
                </Box>                
            </Box>
        );
        setChartOpen(true);
    };

    const handleCloseDialog = () => {
        setSelectedChart(null);
        setChartOpen(false);
    };

    const defaultTitle = `Crew Members Count`;
    const chartMap = {
        crewByRankBase: [
            { component: <SortBarMultiLinesChart data={data.crewByRankBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],
        crewByRank: [
            { component: <SortBarMultiLinesChart data={data.crewByRank} title={defaultTitle} bottomLegend='Rank' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],   
        crewByBase: [
            { component: <SortBarMultiLinesChart data={data.crewByBase} title={defaultTitle} bottomLegend='Base' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ], 
        crewByGender: [
            { component: <SortBarMultiLinesChart data={data.crewByGender} title={defaultTitle} bottomLegend='Gender' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
            { component: <SortStackedBarChart data={data.crewByStackedGender} title={defaultTitle} bottomLegend='Gender' leftLegend='Count' digit={0} bottomOffSet={10} bottomLegendOffSet={10} translateXOffSet={5} />, style: {} },
        ],   
        crewByNationality: [
            { component: <SortBarMultiLinesChart data={data.crewByNationality} title={defaultTitle} bottomLegend='Nationality' leftLegend='Count' isHorizontalBottomLegend={true} digit={0} bottomOffSet={-30} bottomLegendOffSet={-30} barTranslateXOffSet={80} lineTranslateXOffSet={-10} />, style: {} },
        ],           
    }; 

    const renderCharts = (charts, customStyles) => (
        <Box
            ref={scrollRef}
            height={LAYOUT_HEIGHT}  
            display="flex"
            flexDirection="column"
            gap={2}
            sx={{ gridColumn: "span 12", overflowY: 'auto' }}
        >
            {charts.map((chart, index) => (
                <Box
                    key={index}
                    onDoubleClick={() => handleChartOpen(chart)}
                    sx={{ ...styles, ...customStyles }}
                >
                    <Box 
                        height="100%" 
                        width={"100%"} 
                        sx={chart.style}
                    >
                        {chart.component}
                    </Box>
                </Box>
            ))}
        </Box>
    );

    
    return (
        <Box className="main-container-box">                  
            <FlexBetween>
                <Header
                    title="Crew Members"
                    subtitle={loading ? 'Loading...' : displayType === 'chart' ? 'List of Charts' : 'List of Tableaus'}
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <FormControl 
                            size="small" 
                            sx={{ width: { xs: '6rem', sm: '8rem' } }}
                        >
                            <InputLabel>Display</InputLabel>
                            <Select
                                value={displayType}
                                label="Display"
                                onChange={(e) => setDisplayType(e.target.value)}
                            >
                                {displayOptions.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        
                        <FormControl
                            size="small"
                            sx={{ width: { xs: '6rem', sm: '10rem' } }}
                        >
                            <InputLabel id="crew-view-label">View</InputLabel>
                            <Select
                                labelId="crew-view-label"
                                label="View"
                                value={safeView}
                                onChange={(e) => setView(e.target.value)}
                            >
                                {viewOptions[displayType].map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FilterButton
                            filters={filters}
                            defaultFilters={defaultFilters}
                            onClick={handleFilterOpen}
                        />
                    </Box>
                )}                
            </FlexBetween>             
            {loading ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Box sx={{ ...styles, height: LAYOUT_HEIGHT }}>
                    <Typography color="error">
                        Error: {error?.message || ''}
                    </Typography>
                </Box>
            ) : (
                <Box
                    display="grid"                        
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                >
                    {displayType === 'chart' && safeView in chartMap &&
                        renderCharts(chartMap[safeView], { minHeight: LAYOUT_HEIGHT })
                    }
                    {displayType === 'tableau' &&
                        renderCharts(
                            [{ component: <CommonTableau data={tableauData} mode="count" />, style: {} }], 
                            { minHeight: LAYOUT_HEIGHT }
                        )
                    }

                    <ScrollToTop scrollRef={scrollRef} />
                </Box>
            )}
   
            {chartOpen && (
                <ChartDialog
                    open={chartOpen}
                    onClose={handleCloseDialog}
                    chartComponent={selectedChart}
                />
            )}

            {isFilterDialogOpen &&
                <CrewFilterDialog
                    open={isFilterDialogOpen}
                    onClose={handleFilterClose}
                    onApply={handleFilterApply}
                    postOptions={postOptions}
                    baseOptions={baseOptions}
                    trainingQualOptions={trainingQualOptions}
                    isActiveOptions={isActiveOptions}
                    currentFilters={filters}
                />
            }
        </Box>
    );
};

export default FlyingCrewCountChart;
